/**
 * D4 人工報價（代客建單）— 平台調度為現有旅行社客戶手動建單並填價。
 * free-format 特殊規格輸入；建立後客戶帳戶即可見，訂單直接進待派遣（confirmed）。
 * 金額一律 HKD（D10）；受 D2 信用額度約束（超額由後端擋、顯示錯誤）。
 */
import { useMemo, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { UserPlus, Send } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { DateTimeField } from "../../components/order/DateTimeField";
import { isCompleteLocalDateTime } from "../../../../shared/local-datetime";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { fromLocalInput, fmtCurrency } from "../../lib/format";
import { REGIONS, SERVICE_TYPE_META, type Loc, type Region } from "../../../../shared/const";
import { CREATABLE_SERVICE_TYPES, type CreatableServiceType } from "../../../../shared/booking";

const REGION_LABEL: Record<Region, Loc> = {
  taiwan: { "zh-TW": "台灣", "en-US": "Taiwan", "zh-CN": "台湾" },
  hongkong: { "zh-TW": "香港", "en-US": "Hong Kong", "zh-CN": "香港" },
};

type AgencyRow = { id: number; companyName: string; region: string; creditLimit: string | null; status: string };

export default function NewOrderForAgency() {
  const { t } = useLanguage();
  const [, navigate] = useLocation();
  const agenciesQ = trpc.party.listAgencies.useQuery();
  const agencies = (agenciesQ.data ?? []) as AgencyRow[];

  const [agencyId, setAgencyId] = useState<number | null>(null);
  const [serviceType, setServiceType] = useState<CreatableServiceType>("airport_pickup");
  const [region, setRegion] = useState<Region>("hongkong");
  const [vehicleType, setVehicleType] = useState("");
  const [pickupLocal, setPickupLocal] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [passengerName, setPassengerName] = useState("");
  const [passengerPhone, setPassengerPhone] = useState("");
  const [passengerCount, setPassengerCount] = useState(1);
  const [luggageCount, setLuggageCount] = useState(0);
  const [specialRequests, setSpecialRequests] = useState("");
  const [priceHkd, setPriceHkd] = useState<number | "">("");

  const selectedAgency = useMemo(() => agencies.find((a) => a.id === agencyId) ?? null, [agencies, agencyId]);

  const m = trpc.order.createForAgency.useMutation({
    onSuccess: (res) => navigate(`/orders/${res.id}`),
  });

  const valid =
    agencyId !== null &&
    vehicleType.trim() !== "" &&
    isCompleteLocalDateTime(pickupLocal) &&
    pickupLocation.trim() !== "" &&
    passengerName.trim() !== "" &&
    passengerCount >= 1 &&
    typeof priceHkd === "number" &&
    priceHkd > 0;

  const submit = () => {
    if (!valid || agencyId === null || typeof priceHkd !== "number") return;
    m.mutate({
      agencyId,
      serviceType,
      region,
      vehicleType: vehicleType.trim(),
      pickupTime: fromLocalInput(pickupLocal),
      pickupLocation: pickupLocation.trim(),
      dropoffLocation: dropoffLocation.trim() || undefined,
      flightNumber: flightNumber.trim() || undefined,
      passengerName: passengerName.trim(),
      passengerPhone: passengerPhone.trim() || undefined,
      passengerCount,
      luggageCount,
      specialRequests: specialRequests.trim() || undefined,
      priceHkd,
    });
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-medium tracking-tight">
          <UserPlus className="h-6 w-6 text-primary" />
          {t({ "zh-TW": "人工報價（代客建單）", "en-US": "Manual quote", "zh-CN": "人工报价（代客建单）" })}
        </h1>
        <p className="text-[13px] text-muted-foreground">
          {t({
            "zh-TW": "為現有旅行社客戶手動建立訂單並填入報價。建立後客戶帳戶即可看到，訂單直接進入待派遣。金額一律以港幣（HKD）計。",
            "en-US": "Create an order for an existing agency and set the price manually. It appears in the client's account and goes straight to pending dispatch. Priced in HKD.",
            "zh-CN": "为现有旅行社客户手动建立订单并填入报价。建立后客户帐户即可看到，订单直接进入待派遣。金额一律以港币（HKD）计。",
          })}
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 p-5">
          <Field label={t({ "zh-TW": "選擇客戶（旅行社）", "en-US": "Client (agency)", "zh-CN": "选择客户（旅行社）" })} required>
            <Select
              value={agencyId ?? ""}
              onChange={(e) => setAgencyId(e.target.value === "" ? null : Number(e.target.value))}
            >
              <option value="">{t({ "zh-TW": "— 請選擇 —", "en-US": "— Select —", "zh-CN": "— 请选择 —" })}</option>
              {agencies
                .filter((a) => a.status === "active")
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.companyName}（{a.region === "taiwan" ? "TW" : "HK"}）
                  </option>
                ))}
            </Select>
            {selectedAgency?.creditLimit != null && (
              <p className="mt-1 text-[12px] text-muted-foreground">
                {t({ "zh-TW": "信用額度：", "en-US": "Credit limit: ", "zh-CN": "信用额度：" })}
                {fmtCurrency(selectedAgency.creditLimit, "HKD")}
              </p>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t({ "zh-TW": "服務類型", "en-US": "Service", "zh-CN": "服务类型" })} required>
              <Select value={serviceType} onChange={(e) => setServiceType(e.target.value as CreatableServiceType)}>
                {CREATABLE_SERVICE_TYPES.map((s) => (
                  <option key={s} value={s}>
                    {t(SERVICE_TYPE_META[s].label)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t({ "zh-TW": "服務區域", "en-US": "Region", "zh-CN": "服务区域" })} required>
              <Select value={region} onChange={(e) => setRegion(e.target.value as Region)}>
                {REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {t(REGION_LABEL[r])}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t({ "zh-TW": "車型（自由填寫）", "en-US": "Vehicle type", "zh-CN": "车型（自由填写）" })} required>
              <Input
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                placeholder={t({ "zh-TW": "如：7 人座商務車", "en-US": "e.g. 7-seat van", "zh-CN": "如：7 座商务车" })}
              />
            </Field>
            <DateTimeField
              idPrefix="agent-pickup"
              label={{ "zh-TW": "上車時間", "en-US": "Pickup time", "zh-CN": "上车时间" }}
              value={pickupLocal}
              onChange={setPickupLocal}
            />
          </div>

          <Field label={t({ "zh-TW": "上車地點", "en-US": "Pickup location", "zh-CN": "上车地点" })} required>
            <Input value={pickupLocation} onChange={(e) => setPickupLocation(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t({ "zh-TW": "下車地點（選填）", "en-US": "Drop-off (optional)", "zh-CN": "下车地点（选填）" })}>
              <Input value={dropoffLocation} onChange={(e) => setDropoffLocation(e.target.value)} />
            </Field>
            <Field label={t({ "zh-TW": "航班編號（選填）", "en-US": "Flight no. (optional)", "zh-CN": "航班编号（选填）" })}>
              <Input value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t({ "zh-TW": "乘客姓名", "en-US": "Passenger", "zh-CN": "乘客姓名" })} required>
              <Input value={passengerName} onChange={(e) => setPassengerName(e.target.value)} />
            </Field>
            <Field label={t({ "zh-TW": "乘客電話（選填）", "en-US": "Phone (optional)", "zh-CN": "乘客电话（选填）" })}>
              <Input value={passengerPhone} onChange={(e) => setPassengerPhone(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t({ "zh-TW": "人數", "en-US": "Passengers", "zh-CN": "人数" })} required>
              <Input
                type="number"
                min={1}
                value={passengerCount}
                onChange={(e) => setPassengerCount(Math.max(1, Number(e.target.value)))}
              />
            </Field>
            <Field label={t({ "zh-TW": "行李件數", "en-US": "Luggage", "zh-CN": "行李件数" })}>
              <Input
                type="number"
                min={0}
                value={luggageCount}
                onChange={(e) => setLuggageCount(Math.max(0, Number(e.target.value)))}
              />
            </Field>
          </div>

          <Field label={t({ "zh-TW": "特殊需求 / 備註", "en-US": "Notes", "zh-CN": "特殊需求 / 备注" })}>
            <Textarea value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} rows={2} />
          </Field>

          <Field label={t({ "zh-TW": "人工報價（HKD）", "en-US": "Manual price (HKD)", "zh-CN": "人工报价（HKD）" })} required>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[13px] text-muted-foreground">HKD</span>
              <Input
                type="number"
                min={1}
                value={priceHkd}
                onChange={(e) => setPriceHkd(e.target.value === "" ? "" : Number(e.target.value))}
              />
            </div>
          </Field>

          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}

          <div className="flex justify-end border-t border-border pt-4">
            <Button size="lg" disabled={!valid || m.isPending} onClick={submit}>
              <Send className="h-4 w-4" />
              {m.isPending
                ? t({ "zh-TW": "建立中…", "en-US": "Creating…", "zh-CN": "建立中…" })
                : t({ "zh-TW": "建立訂單並報價", "en-US": "Create & quote", "zh-CN": "建立订单并报价" })}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}
